import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const STATUS_OPTIONS = ['All', 'Pending', 'Confirmed', 'Completed', 'Cancelled'];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_NL = { monday: 'Maandag', tuesday: 'Dinsdag', wednesday: 'Woensdag', thursday: 'Donderdag', friday: 'Vrijdag', saturday: 'Zaterdag', sunday: 'Zondag' };
const PERIODS = ['morning', 'afternoon', 'evening'];
const PERIOD_NL = { morning: 'Ochtend', afternoon: 'Middag', evening: 'Avond' };
const PERIOD_DEFAULTS = {
  morning_start: '09:00', morning_end: '12:00',
  afternoon_start: '13:00', afternoon_end: '17:00',
  evening_start: '17:00', evening_end: '20:00',
};

function StatusBadge({ status }) {
  const cls = { Pending: 'badge-pending', Confirmed: 'badge-confirmed', Completed: 'badge-completed', Cancelled: 'badge-cancelled' };
  return <span className={cls[status] || 'badge-pending'}>{status}</span>;
}

const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

function WeekCalendar({ appointments, onSelect }) {
  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];

  const [weekOffset, setWeekOffset] = React.useState(0);
  const [calView, setCalView] = React.useState('week'); // 'week' | 'month'
  const [monthRef, setMonthRef] = React.useState(new Date(today.getFullYear(), today.getMonth(), 1));

  // Week view: compute days for offset week
  const getWeekDays = () => {
    const base = new Date(today);
    base.setDate(today.getDate() - today.getDay() + 1 + weekOffset * 7); // Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return d;
    });
  };

  const apptsByDay = {};
  appointments.forEach(a => {
    if (!apptsByDay[a.appointment_date]) apptsByDay[a.appointment_date] = [];
    apptsByDay[a.appointment_date].push(a);
  });

  const statusColor = { Confirmed: '#dcfce7', Pending: '#fef3c7', Cancelled: '#f3f4f6', Completed: '#dbeafe' };
  const statusBorder = { Confirmed: '#16a34a', Pending: '#d97706', Cancelled: '#9ca3af', Completed: 'var(--brand)' };

  const AppointmentChip = ({ a }) => (
    <button onClick={() => onSelect(a)}
      className="w-full text-left p-2 rounded-lg text-xs transition-all hover:shadow-sm"
      style={{ backgroundColor: statusColor[a.status] || '#fef3c7', borderLeft: `3px solid ${statusBorder[a.status] || '#d97706'}` }}>
      <p className="font-semibold truncate">{a.client_name.split(' ')[0]}</p>
      <p className="text-gray-600 mt-0.5">{a.appointment_time}</p>
      <span>{a.appointment_type === 'Video call' ? '📹' : '🏢'}</span>
    </button>
  );

  if (calView === 'week') {
    const days = getWeekDays();
    const weekLabel = `${days[0].getDate()} ${MONTHS_NL[days[0].getMonth()]} – ${days[6].getDate()} ${MONTHS_NL[days[6].getMonth()]} ${days[6].getFullYear()}`;
    return (
      <div className="card overflow-hidden flex flex-col flex-1">
        {/* Navigation */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <button onClick={() => setWeekOffset(w => w - 1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg leading-none">‹</button>
            <button onClick={() => setWeekOffset(0)}
              className="px-3 py-1 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200">
              Vandaag
            </button>
            <button onClick={() => setWeekOffset(w => w + 1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg leading-none">›</button>
          </div>
          <span className="text-sm font-semibold text-gray-700">{weekLabel}</span>
          <button onClick={() => setCalView('month')}
            className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-100">
            Maandoverzicht
          </button>
        </div>
        <div className="grid grid-cols-7 border-b border-gray-200">
          {days.map((d, i) => {
            const isToday = fmt(d) === fmt(today);
            return (
              <div key={i} className={`py-3 px-2 text-center border-r border-gray-100 last:border-r-0 ${isToday ? 'bg-blue-50' : ''}`}>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {d.toLocaleDateString('nl-NL', { weekday: 'short' })}
                </p>
                <p className={`text-lg font-bold mt-0.5 ${isToday ? 'text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto' : 'text-gray-800'}`}
                  style={isToday ? { backgroundColor: 'var(--brand)' } : {}}>
                  {d.getDate()}
                </p>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-7 flex-1">
          {days.map((d, i) => {
            const dayAppts = apptsByDay[fmt(d)] || [];
            return (
              <div key={i} className="border-r border-gray-100 last:border-r-0 p-2 space-y-1.5">
                {dayAppts.map(a => <AppointmentChip key={a.id} a={a} />)}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Month view
  const year = monthRef.getFullYear();
  const month = monthRef.getMonth();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return (
    <div className="card overflow-hidden">
      {/* Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonthRef(new Date(year, month - 1, 1))}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg leading-none">‹</button>
          <button onClick={() => setMonthRef(new Date(today.getFullYear(), today.getMonth(), 1))}
            className="px-3 py-1 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200">
            Vandaag
          </button>
          <button onClick={() => setMonthRef(new Date(year, month + 1, 1))}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg leading-none">›</button>
        </div>
        <span className="text-sm font-semibold text-gray-700">{MONTHS_NL[month]} {year}</span>
        <button onClick={() => setCalView('week')}
          className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-100">
          Weekoverzicht
        </button>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(d => (
          <div key={d} className="py-2 text-center text-xs font-medium text-gray-400">{d}</div>
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-7">
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="border-b border-r border-gray-100 min-h-20" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isToday = dateStr === fmt(today);
          const dayAppts = apptsByDay[dateStr] || [];
          return (
            <div key={day} className={`border-b border-r border-gray-100 last:border-r-0 p-1.5 min-h-20 ${isToday ? 'bg-blue-50' : ''}`}>
              <p className={`text-xs font-bold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'text-white' : 'text-gray-600'}`}
                style={isToday ? { backgroundColor: 'var(--brand)' } : {}}>
                {day}
              </p>
              <div className="space-y-0.5">
                {dayAppts.slice(0, 3).map(a => (
                  <button key={a.id} onClick={() => onSelect(a)}
                    className="w-full text-left px-1.5 py-0.5 rounded text-xs truncate font-medium"
                    style={{ backgroundColor: statusColor[a.status] || '#fef3c7', borderLeft: `2px solid ${statusBorder[a.status] || '#d97706'}` }}>
                    {a.appointment_time} {a.client_name.split(' ')[0]}
                  </button>
                ))}
                {dayAppts.length > 3 && (
                  <p className="text-xs text-gray-400 pl-1">+{dayAppts.length - 3} meer</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailPanel({ appointment, onClose, onRefresh, authFetch }) {
  const [aiContent, setAiContent] = useState({ confirmation: '', prep: '', reminder: '' });
  const [loading, setLoading] = useState({ confirmation: false, prep: false, reminder: false });
  const [sending, setSending] = useState({});
  const [sendResult, setSendResult] = useState({});
  const [copied, setCopied] = useState({});

  const generate = async (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    try {
      const endpoint = type === 'prep' ? 'generate-prep' : type === 'confirmation' ? 'generate-confirmation' : 'generate-reminder';
      const res = await authFetch(`/api/appointments/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify({ appointment_id: appointment.id }),
      });
      const data = await res.json();
      const content = data.email || data.brief || '';
      setAiContent(prev => ({ ...prev, [type]: content }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const copy = (type, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(prev => ({ ...prev, [type]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [type]: false })), 2000);
    });
  };

  const sendEmail = async (type) => {
    const text = aiContent[type];
    if (!text) return;
    setSending(prev => ({ ...prev, [type]: true }));
    setSendResult(prev => ({ ...prev, [type]: '' }));
    try {
      const subjectMap = {
        confirmation: `Bevestiging afspraak ${appointment.appointment_date}`,
        reminder:     `Herinnering: afspraak morgen om ${appointment.appointment_time}`,
        prep:         null, // prep brief is for internal use, not sent
      };
      const res = await authFetch('/api/appointments/send-email', {
        method: 'POST',
        body: JSON.stringify({
          appointment_id: appointment.id,
          subject: subjectMap[type],
          body: text,
        }),
      });
      const data = await res.json();
      setSendResult(prev => ({ ...prev, [type]: res.ok ? 'sent' : data.error || 'Mislukt' }));
    } catch {
      setSendResult(prev => ({ ...prev, [type]: 'Mislukt' }));
    } finally {
      setSending(prev => ({ ...prev, [type]: false }));
    }
  };

  const updateStatus = async (status) => {
    const endpoint = status === 'Confirmed' ? 'confirm' : status === 'Cancelled' ? 'cancel' : 'complete';
    await authFetch(`/api/appointments/${endpoint}/${appointment.id}`, { method: 'POST' });
    onRefresh();
    onClose();
  };

  if (!appointment) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-gray-900">Afspraak details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Client info */}
          <div>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{appointment.client_name}</h3>
                <p className="text-sm text-gray-500">{appointment.client_email}</p>
                {appointment.client_phone && <p className="text-sm text-gray-500">{appointment.client_phone}</p>}
              </div>
              <StatusBadge status={appointment.status} />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Datum & tijd</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">
                  {new Date(appointment.appointment_date).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="text-sm text-gray-600">{appointment.appointment_time} ({appointment.duration} min)</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Type</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">
                  {appointment.appointment_type === 'Video call' ? '📹 Video call' : '🏢 Op locatie'}
                </p>
              </div>
            </div>

            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Reisinteresse</p>
              <p className="text-sm text-gray-700">{appointment.trip_interest}</p>
            </div>
          </div>

          {/* Status actions */}
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Status bijwerken</p>
            <div className="flex flex-wrap gap-2">
              {appointment.status !== 'Confirmed' && appointment.status !== 'Cancelled' && (
                <button onClick={() => updateStatus('Confirmed')} className="btn-secondary text-sm">
                  ✅ Bevestigen
                </button>
              )}
              {appointment.status !== 'Completed' && appointment.status !== 'Cancelled' && (
                <button onClick={() => updateStatus('Completed')} className="btn-secondary text-sm">
                  🏁 Afronden
                </button>
              )}
              {appointment.status !== 'Cancelled' && (
                <button onClick={() => updateStatus('Cancelled')} className="btn-danger text-sm">
                  ✕ Annuleren
                </button>
              )}
            </div>
          </div>

          {/* AI tools */}
          {[
            { key: 'confirmation', label: 'Bevestigingsmail genereren', icon: '📧', btnLabel: 'Genereer bevestigingsmail' },
            { key: 'prep', label: 'Prep brief genereren', icon: '📋', btnLabel: 'Genereer prep brief' },
            { key: 'reminder', label: 'Herinneringsmail genereren', icon: '🔔', btnLabel: 'Genereer herinnering' },
          ].map(({ key, label, icon, btnLabel }) => (
            <div key={key} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{icon}</span>
                  <h4 className="font-medium text-gray-800 text-sm">{label}</h4>
                </div>
                <button onClick={() => generate(key)} disabled={loading[key]}
                  className="btn-primary text-xs px-3 py-1.5">
                  {loading[key] ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
                  {loading[key] ? 'Genereren...' : btnLabel}
                </button>
              </div>
              {aiContent[key] && (
                <div>
                  {key === 'prep' ? (
                    <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed font-mono text-xs">
                      {aiContent[key]}
                    </div>
                  ) : (
                    <textarea className="input text-sm min-h-32"
                      value={aiContent[key]}
                      onChange={e => setAiContent(prev => ({ ...prev, [key]: e.target.value }))} />
                  )}
                  <div className="flex gap-2 mt-2 flex-wrap items-center">
                    <button onClick={() => copy(key, aiContent[key])} className="btn-secondary text-xs">
                      {copied[key] ? '✅ Gekopieerd' : '📋 Kopieer'}
                    </button>
                    {key !== 'prep' && (
                      <button
                        onClick={() => sendEmail(key)}
                        disabled={sending[key]}
                        className="btn-primary text-xs px-3 py-1.5">
                        {sending[key] ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '📧'}
                        {sending[key] ? ' Verzenden...' : ` Stuur naar ${appointment.client_email}`}
                      </button>
                    )}
                    {sendResult[key] === 'sent' && (
                      <span className="text-xs text-green-600 font-medium">✅ Verzonden</span>
                    )}
                    {sendResult[key] && sendResult[key] !== 'sent' && (
                      <span className="text-xs text-red-600">{sendResult[key]}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AvailabilityModal({ onClose, authFetch }) {
  const [avail, setAvail] = useState(null);
  const [blackouts, setBlackouts] = useState([]);
  const [newBlackout, setNewBlackout] = useState('');
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    authFetch('/api/appointments/availability/settings')
      .then(r => r.json())
      .then(data => {
        const a = data.availability || {};
        // Apply defaults for time range fields if not yet stored
        setAvail({
          ...PERIOD_DEFAULTS,
          ...a,
        });
        setBlackouts(data.blackout_dates || []);
      });
  }, []);

  const save = async () => {
    setSaveError('');
    try {
      const res = await authFetch('/api/appointments/availability/settings', {
        method: 'POST',
        body: JSON.stringify({ availability: avail, blackout_dates: blackouts }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSaveError(data.error || 'Opslaan mislukt');
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setSaveError('Opslaan mislukt');
    }
  };

  if (!avail) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto">
      <div className="bg-white rounded-2xl p-8"><div className="spinner spinner-blue" /></div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 overflow-y-auto p-8">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-bold text-gray-900">Beschikbaarheid instellen</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="p-6">
          {/* Time ranges per period */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Tijdvensters per dagdeel</h3>
            <div className="space-y-3">
              {PERIODS.map(period => (
                <div key={period} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="w-20 text-sm font-medium text-gray-700">{PERIOD_NL[period]}</span>
                  <div className="flex items-center gap-2">
                    <input type="time" className="input py-1 px-2 text-sm w-28"
                      value={avail[`${period}_start`] || PERIOD_DEFAULTS[`${period}_start`]}
                      onChange={e => setAvail(prev => ({ ...prev, [`${period}_start`]: e.target.value }))} />
                    <span className="text-gray-400 text-sm">–</span>
                    <input type="time" className="input py-1 px-2 text-sm w-28"
                      value={avail[`${period}_end`] || PERIOD_DEFAULTS[`${period}_end`]}
                      onChange={e => setAvail(prev => ({ ...prev, [`${period}_end`]: e.target.value }))} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Weekly grid */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Wekelijks schema</h3>
            <div className="space-y-2">
              {DAYS.map(day => (
                <div key={day} className="flex items-center gap-4">
                  <span className="w-24 text-sm text-gray-600">{DAY_NL[day]}</span>
                  <div className="flex gap-3">
                    {PERIODS.map(period => {
                      const key = `${day}_${period}`;
                      const startT = avail[`${period}_start`] || PERIOD_DEFAULTS[`${period}_start`];
                      const endT   = avail[`${period}_end`]   || PERIOD_DEFAULTS[`${period}_end`];
                      return (
                        <label key={period} className="flex items-center gap-1.5 cursor-pointer">
                          <input type="checkbox" className="accent-blue-600"
                            checked={!!avail[key]}
                            onChange={e => setAvail(prev => ({ ...prev, [key]: e.target.checked ? 1 : 0 }))} />
                          <span className="text-xs text-gray-600">{PERIOD_NL[period]} ({startT}–{endT})</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slot settings */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="label">Tijdslot duur</label>
              <div className="flex gap-3">
                {[30, 60].map(d => (
                  <label key={d} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="slot_duration" className="accent-blue-600"
                      checked={avail.slot_duration === d}
                      onChange={() => setAvail(prev => ({ ...prev, slot_duration: d }))} />
                    <span className="text-sm">{d} min</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="label">Buffer tussen afspraken</label>
              <select className="input" value={avail.buffer_time || 15}
                onChange={e => setAvail(prev => ({ ...prev, buffer_time: parseInt(e.target.value) }))}>
                {[0, 15, 30].map(b => <option key={b} value={b}>{b} min</option>)}
              </select>
            </div>
          </div>

          {/* Blackout dates */}
          <div className="mb-6">
            <label className="label">Geblokkeerde datums</label>
            <div className="flex gap-2 mb-2">
              <input type="date" className="input" value={newBlackout} onChange={e => setNewBlackout(e.target.value)} />
              <button onClick={() => { if (newBlackout && !blackouts.includes(newBlackout)) { setBlackouts([...blackouts, newBlackout]); setNewBlackout(''); } }}
                className="btn-secondary">
                Toevoegen
              </button>
            </div>
            {blackouts.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {blackouts.map(d => (
                  <span key={d} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-700">
                    {d}
                    <button onClick={() => setBlackouts(blackouts.filter(b => b !== d))} className="text-red-400 hover:text-red-600 ml-1">✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {saveError && (
            <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{saveError}</div>
          )}
          <button onClick={save} className="btn-primary">
            {saved ? '✅ Opgeslagen!' : 'Beschikbaarheid opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Appointments() {
  const { authFetch } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [viewMode, setViewMode] = useState('calendar');
  const [selected, setSelected] = useState(null);
  const [showAvailability, setShowAvailability] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'All' ? `?status=${statusFilter}` : '';
      const res = await authFetch(`/api/appointments${params}`);
      if (res.ok) setAppointments(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  return (
    <div className="flex flex-col page-enter" style={{ height: '100%', padding: '2rem' }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Afspraken</h1>
          <p className="text-gray-500 text-sm mt-1">Beheer uw klantafspraken</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAvailability(true)} className="btn-secondary">
            📅 Beschikbaarheid instellen
          </button>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {[['calendar', '📅 Kalender'], ['list', '📋 Lijst']].map(([mode, label]) => (
              <button key={mode} onClick={() => setViewMode(mode)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === mode ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                style={viewMode === mode ? { backgroundColor: 'var(--brand)' } : {}}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {STATUS_OPTIONS.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${statusFilter === s ? 'text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-300'}`}
            style={statusFilter === s ? { backgroundColor: 'var(--brand)' } : {}}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card flex items-center justify-center py-16">
          <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
        </div>
      ) : viewMode === 'calendar' ? (
        <div className="flex-1 flex flex-col min-h-0">
          <WeekCalendar appointments={appointments} onSelect={setSelected} />
        </div>
      ) : (
        <div className="card overflow-hidden">
          {appointments.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-4xl mb-3">📅</div>
              <p>Geen afspraken gevonden</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr>
                  {['Klant', 'Datum & tijd', 'Type', 'Reisinteresse', 'Status', 'Acties'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {appointments.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{a.client_name}</p>
                      <p className="text-xs text-gray-400">{a.client_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <p>{new Date(a.appointment_date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}</p>
                      <p className="text-xs">{a.appointment_time}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600">{a.appointment_type === 'Video call' ? '📹' : '🏢'} {a.appointment_type}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{a.trip_interest}</td>
                    <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(a)} className="text-sm font-medium" style={{ color: 'var(--brand)' }}>
                        Bekijken →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {selected && (
        <DetailPanel
          appointment={selected}
          onClose={() => setSelected(null)}
          onRefresh={load}
          authFetch={authFetch}
        />
      )}

      {showAvailability && (
        <AvailabilityModal onClose={() => setShowAvailability(false)} authFetch={authFetch} />
      )}
    </div>
  );
}
